import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { IconVault } from '@/components/Icons';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getVaultConfig, initializeVault, isVaultUnlocked } from '@/services/vaultService';

export default function VaultUnlockDialog({ open, onClose, onUnlocked }) {
  const [password, setPassword] = useState('');
  const [claudeKey, setClaudeKey] = useState('');
  const [geminiKey, setGeminiKey] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showClaude, setShowClaude] = useState(false);
  const [showGemini, setShowGemini] = useState(false);
  const [unlocking, setUnlocking] = useState(false);

  const handleUnlock = async () => {
    if (!password) { toast.error('Enter your vault password'); return; }
    if (!claudeKey) { toast.error('Enter your Claude API key'); return; }
    if (!geminiKey) { toast.error('Enter your Gemini API key'); return; }

    setUnlocking(true);
    try {
      const config = await getVaultConfig();
      if (!config.exists || !config.salt) {
        toast.error('No vault found for this account');
        return;
      }

      await initializeVault(password, config.salt, 'vault', {
        claudeApiKey: claudeKey,
        geminiApiKey: geminiKey,
      });

      if (isVaultUnlocked()) {
        toast.success('Vault unlocked');
        setPassword(''); setClaudeKey(''); setGeminiKey('');
        onUnlocked?.();
        onClose();
      } else {
        toast.error('Failed to unlock — check your password');
      }
    } catch (err) {
      console.error('Vault unlock error:', err);
      toast.error('Incorrect password or vault error');
    } finally {
      setUnlocking(false);
    }
  };

  const KeyInput = ({ label, value, onChange, show, onToggle, placeholder }) => (
    <div>
      <Label className="text-[var(--text-secondary)] text-xs">{label}</Label>
      <div className="relative mt-1">
        <Input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="bg-[var(--bg-app)] border-[var(--border-default)] text-[var(--text-primary)] pr-10"
          onKeyDown={(e) => e.key === 'Enter' && handleUnlock()}
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-disabled)] hover:text-[var(--text-secondary)]"
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[var(--bg-elevated)] border-[var(--border-subtle)] max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[var(--text-primary)]">
            <IconVault size={22} active />
            Unlock Vault
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs text-[var(--text-muted)] -mt-2">
          Enter your vault password and API keys to decrypt your projects.
          Stored in memory only — never sent to 1895 servers.
        </p>

        <div className="space-y-3 mt-2">
          <KeyInput
            label="Vault Password"
            value={password} onChange={setPassword}
            show={showPassword} onToggle={() => setShowPassword(!showPassword)}
            placeholder="Your vault password"
          />
          <KeyInput
            label="Claude API Key (analysis)"
            value={claudeKey} onChange={setClaudeKey}
            show={showClaude} onToggle={() => setShowClaude(!showClaude)}
            placeholder="sk-ant-..."
          />
          <KeyInput
            label="Gemini API Key (images)"
            value={geminiKey} onChange={setGeminiKey}
            show={showGemini} onToggle={() => setShowGemini(!showGemini)}
            placeholder="AIza..."
          />

          <Button
            onClick={handleUnlock}
            disabled={unlocking}
            className="w-full bg-amber-500 hover:bg-amber-600 text-black font-semibold"
          >
            {unlocking ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
            Unlock Vault
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
